// Mileage rates, ported from the app's MileageRates. Amounts are kept as exact ten-thousandths
// (the server stores numeric(12, 4)) so values like 25 mi × $0.205 come out as $5.125, not
// 5.1249999…, before rounding.

export type DistanceUnit = "miles" | "kilometers";

export const metersPerUnit: Record<DistanceUnit, number> = {
  miles: 1609.344,
  kilometers: 1000,
};

export function unitFrom(raw: string | null | undefined): DistanceUnit {
  return raw === "kilometers" ? "kilometers" : "miles";
}

/** The month and day a rate starts within its year, as calendar values. */
export interface RateStart {
  month: number;
  day: number;
}

export const yearStart: RateStart = { month: 1, day: 1 };

export function compareStarts(a: RateStart, b: RateStart): number {
  return a.month - b.month || a.day - b.day;
}

export interface RateEntry {
  year: number;
  start: RateStart;
  /** The category's raw value. */
  category: string;
  /** Nil when the user cleared the rate. */
  amount: number | null;
  unit: DistanceUnit;
  currencyCode: string;
  updatedAt: Date;
}

export interface ResolvedRate {
  /** The year the rate was set for; differs from the trip's year when borrowed. */
  year: number;
  start: RateStart;
  /** In ten-thousandths of the currency, exact. */
  amountTenThousandths: number;
  unit: DistanceUnit;
  currencyCode: string;
  isFallback: boolean;
}

/** The rate as a number, e.g. 0.725. */
export function rateAmount(rate: ResolvedRate): number {
  return rate.amountTenThousandths / 10_000;
}

/** The value of a distance at a rate, unrounded so totals round once. */
export function rateValue(rate: ResolvedRate, meters: number): number {
  return (meters / metersPerUnit[rate.unit]) * rate.amountTenThousandths / 10_000;
}

function key(year: number, start: RateStart, category: string): string {
  return `${year}|${start.month}|${start.day}|${category}`;
}

export class MileageRates {
  private readonly entries = new Map<string, RateEntry>();
  /** Years with at least one entry, newest first. */
  private readonly years: number[];
  /** Each year's starts, earliest first. */
  private readonly startsByYear = new Map<number, RateStart[]>();

  constructor(entries: readonly RateEntry[]) {
    // One entry per year, start and category: the newest when sync left duplicates.
    for (const entry of entries) {
      const k = key(entry.year, entry.start, entry.category);
      const existing = this.entries.get(k);
      if (existing && existing.updatedAt.getTime() >= entry.updatedAt.getTime()) continue;
      this.entries.set(k, entry);
    }
    const years = new Set<number>();
    for (const entry of this.entries.values()) {
      years.add(entry.year);
      const starts = this.startsByYear.get(entry.year) ?? [];
      if (!starts.some((start) => compareStarts(start, entry.start) === 0)) starts.push(entry.start);
      this.startsByYear.set(entry.year, starts);
    }
    for (const starts of this.startsByYear.values()) starts.sort(compareStarts);
    this.years = [...years].sort((a, b) => b - a);
  }

  /**
   * The rate for a category on a date (in local time): the latest period of the date's year that
   * has started, or the final rate of the latest earlier year with rates when its own has none.
   */
  rate(category: string, date: Date): ResolvedRate | undefined {
    const year = date.getFullYear();
    const ratesYear = this.years.find((candidate) => candidate <= year);
    if (ratesYear === undefined) return undefined;
    const starts = this.startsByYear.get(ratesYear) ?? [];
    let start: RateStart | undefined;
    if (ratesYear === year) {
      const tripDay = { month: date.getMonth() + 1, day: date.getDate() };
      start = starts.filter((candidate) => compareStarts(candidate, tripDay) <= 0).at(-1);
    } else {
      start = starts.at(-1);
    }
    if (!start) return undefined;
    const entry = this.entries.get(key(ratesYear, start, category));
    if (!entry || entry.amount === null) return undefined;
    return {
      year: ratesYear,
      start,
      amountTenThousandths: Math.round(entry.amount * 10_000),
      unit: entry.unit,
      currencyCode: entry.currencyCode,
      isFallback: ratesYear !== year,
    };
  }

  /** What a trip is worth, or undefined when it's unclassified or its category has no rate. */
  deduction(meters: number, category: string | undefined, date: Date): number | undefined {
    if (!category) return undefined;
    const rate = this.rate(category, date);
    return rate ? rateValue(rate, meters) : undefined;
  }

  /** Every stored rate that has an amount, for "Rates used" lists. */
  get all(): RateEntry[] {
    return [...this.entries.values()].filter((entry) => entry.amount !== null);
  }

  /** The one currency all rates use, or undefined when they mix. */
  get currencyCode(): string | undefined {
    const codes = new Set(this.all.map((entry) => entry.currencyCode).filter(Boolean));
    return codes.size === 1 ? [...codes][0] : undefined;
  }
}

/** Money to the cent with halves rounded away from zero, as a receipt does ($5.125 is $5.13). */
export function roundMoney(value: number): number {
  const cents = Math.abs(value) * 100;
  // The nudge absorbs floating-point dust such as 512.49999999999994.
  return (Math.sign(value) * Math.round(cents + 1e-7)) / 100;
}
